/**
 * Pure utilities for the book-processing-progress feature (v0.12.0 schema).
 *
 * Derives the values shown on the progress bar from a `ProjectProgressResponse`
 * returned by GET /projects/{id}/progress.
 *
 * Three-bucket formula (weights sum to 100):
 *   Preparatory  (30) — fraction of files with Phase 1 complete (file_status === 'done')
 *   Process      (50) — fraction of chapters with narration audio
 *   Blitz        (20) — fraction of chapters with key points
 */

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

export function computePreparatoryBucket(files) {
  if (!files || files.length === 0) return 0;
  const ready = files.filter((f) => f.file_status === 'done').length;
  return (ready / files.length) * 30;
}

export function computeProcessBucket(files) {
  if (!files || files.length === 0) return 0;
  const totalChapters = files.reduce((s, f) => s + (f.chapter_count || 0), 0);
  if (totalChapters === 0) return 0;
  const processed = files.reduce((s, f) => s + (f.processed_chapter_count || 0), 0);
  return (processed / totalChapters) * 50;
}

export function computeBlitzBucket(files) {
  if (!files || files.length === 0) return 0;
  const totalChapters = files.reduce((s, f) => s + (f.chapter_count || 0), 0);
  if (totalChapters === 0) return 0;
  const blitzed = files.reduce((s, f) => s + (f.blitzed_chapter_count || 0), 0);
  return (blitzed / totalChapters) * 20;
}

// The per-file counters lag: a file can report every chapter processed while a
// freshly extracted chapter of its own is not counted yet. The chapter rows are
// ground truth for the work that has actually landed, so when they are
// available take whichever source claims less — the bar must never overstate.
function computeChapterListTotal(files, chapters) {
  if (!chapters || chapters.length === 0) return null;
  const withAudio = chapters.filter((c) => c.has_chapter_audio).length;
  const withKeyPoints = chapters.filter((c) => c.has_key_points).length;
  return (
    computePreparatoryBucket(files) +
    (withAudio / chapters.length) * 50 +
    (withKeyPoints / chapters.length) * 20
  );
}

export function deriveOverallPercent(files, chapters) {
  if (!files || files.length === 0) return 0;
  const counterTotal =
    computePreparatoryBucket(files) +
    computeProcessBucket(files) +
    computeBlitzBucket(files);
  const listTotal = computeChapterListTotal(files, chapters);
  const total = listTotal === null ? counterTotal : Math.min(counterTotal, listTotal);
  return clamp(Math.round(total), 0, 100);
}

const STAGE_LABEL_BY_STATE = (audioState, blitzState) => {
  if (blitzState === 'in_progress') return 'Generating key points';
  if (audioState === 'in_progress') return 'Generating chapter audio';
  // Nothing is running. Either both phases finished (and the bar is only
  // visible because a chapter is still unprocessed — it is queued, waiting for
  // a worker), or the blitz phase has yet to be picked up.
  if (audioState === 'done') {
    return blitzState === 'done' ? 'Waiting to process' : 'Generating key points';
  }
  if (blitzState === 'done') return 'Generating key points';
  return 'Preparing chapters';
};

export function deriveBarState(snapshot, chapters) {
  const files = snapshot?.files ?? [];
  const audioState = snapshot?.audio_state ?? 'not_started';
  const blitzState = snapshot?.blitz_state ?? 'not_started';
  const isError = audioState === 'failed' || blitzState === 'failed';
  return {
    percent: deriveOverallPercent(files, chapters),
    currentStageLabel: STAGE_LABEL_BY_STATE(audioState, blitzState),
    appearance: isError ? 'error' : 'normal',
    failedStage: isError ? { error_message: null } : null,
  };
}
